"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  src: string;
  alt: string;
  className?: string;
  loading?: "eager" | "lazy";
};

function toUsableSrc(src: string): string {
  if (!src) return "";

  // Absolute (Cloud Run / CDN)
  if (src.startsWith("http://") || src.startsWith("https://")) return src;

  // Same-origin API route (your Next proxy routes)
  if (src.startsWith("/api/")) return src;

  // Same-origin public assets (like /hero/...)
  if (src.startsWith("/")) return src;

  return "";
}

export default function SmartImg({ src, alt, className, loading = "lazy" }: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  const usableSrc = useMemo(() => toUsableSrc(src), [src]);

  const finalSrc = useMemo(() => {
    if (!usableSrc) return "";
    if (retry === 0) return usableSrc;

    const u = new URL(usableSrc, window.location.origin);
    u.searchParams.set("v", String(Date.now()));
    return u.toString();
  }, [usableSrc, retry]);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    setRetry(0);
  }, [usableSrc]);

  if (!usableSrc || failed) {
    return (
      <div
        className={[
          "flex h-full w-full items-center justify-center",
          "bg-neutral-100 text-xs text-neutral-500",
          "dark:bg-neutral-800 dark:text-neutral-400",
          className ?? "",
        ].join(" ")}
      >
        Image unavailable
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      {!loaded && <div className="absolute inset-0 animate-pulse bg-neutral-100 dark:bg-neutral-800" />}

      <img
        ref={imgRef}
        src={finalSrc}
        alt={alt}
        className={[className ?? "", loaded ? "" : "opacity-0"].join(" ")}
        loading={loading}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (retry === 0) setRetry(1);
          else setFailed(true);
        }}
      />
    </div>
  );
}
